/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片列表组件（使用新的 Redux Slice）
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-05-21 10:43:12
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { Empty, Checkbox, message } from 'antd'
import { AutoSizer, Grid } from 'react-virtualized'
import { setLoading } from '../../actions/main'
import Viewer from '../components/ViewerV2'
import Photo from '../components/Photo'
import PhotoWithTag from '../components/PhotoWithTag'
import HeaderExtra from './components/HeaderExtra'
import { BatchTagModeProvider, BatchTagBar } from '../components/BatchTagMode'
import { useTeamPhotoTags } from '../../app/hooks/useTeamPhotoTags'
import { useBatchTagHandlers } from '../photoTag/hooks/useBatchTagHandlers'
import FuzzyMatch from './components/FuzzyMatch'
import backTop from '../../images/back_top.png'
import { capturePromiseException } from '../../utils/sentryUtils'
import {
  trackPicsPageClickV2,
  trackMutualExclusiveSelectionToast
} from './utils/track'
import { buildFetchPhotosParamsWithOptions } from './lib/photo'
import { sensorsRole } from '../../utils/sensors'
import {
  fetchPhotosV2,
  fetchMorePhotosV2,
  fetchSuggestedPhotosV2,
  fetchMoreSuggestedPhotosV2,
  fetch90DaysPhotosCountV2,
  pushCheckedPhoto,
  removeCheckedPhoto,
  pushUncheckedPhoto,
  removeUncheckedPhoto,
  setCheckAll,
  clearCheck,
  selectPhotos,
  selectPhotosNum,
  selectExpandInfo,
  selectLastPageCond,
  selectCheckedPhotos,
  selectUncheckedPhotos,
  selectCheckAll,
  selectCanFavorite,
  selectIsGroupPhotoViewLimit,
  selectOldPhotosCount,
  updatePhotoFavorite,
  setIsAscSort as setContentIsAscSort,
  // 猜你想搜相关
  selectSuggestedWords,
  selectSuggestedPhotos,
  selectSuggestedPhotosNum,
  selectSuggestedLastPageCond,
  selectSuggestedCheckedPhotos,
  selectSuggestedUncheckedPhotos,
  selectSuggestedCheckAll,
  updateSuggestedWordChecked,
  pushSuggestedCheckedPhoto,
  removeSuggestedCheckedPhoto,
  pushSuggestedUncheckedPhoto,
  removeSuggestedUncheckedPhoto,
  setSuggestedCheckAll,
  clearSuggestedCheck,
  selectFallbackType,
  selectFallbackKeyword,
  selectSuggestedQueryFilter
} from '../../slices/photosByContentSlice'
import { selectSearchFilter } from '../../slices/photoFilterSlice'
import { trackExposureSensorEvent } from '../../utils/photoUtils'
import { getSensorContent } from '../../utils/photoUtils'
import { trackFilterSuccess } from '../components/PhotoFilter/utils/sensorUtils'
import OpenEnterprise from './components/OpenEnterprise'
import OpenVip from './components/OpenVip'
import SearchFeedback from './components/SearchFeedback'
import {
  normalizeToSet,
  buildSuggestedPhotosQueryFilter
} from './utils/selectionHelpers'
import type { RootState } from '../../app/store'
import type { Photo as PhotoType } from './types'

/**
 * 照片列表组件
 */
interface PhotosListProps {
  photoListRef: React.RefObject<any>
  searchType?: '普通猜你想搜' | '向量化猜你想搜'
}

const PhotosList: React.FC<PhotosListProps> = ({
  photoListRef,
  searchType = '普通猜你想搜'
}) => {
  const dispatch = useDispatch()
  const viewerRef = useRef<any>(null)
  const suggestedViewerRef = useRef<any>(null)

  // 获取状态
  const photos = useSelector(selectPhotos)
  const photosNum = useSelector(selectPhotosNum)
  const expandInfo = useSelector(selectExpandInfo)
  const lastPageCond = useSelector(selectLastPageCond)
  const checkedPhotosRaw = useSelector(selectCheckedPhotos)
  const unCheckedPhotosRaw = useSelector(selectUncheckedPhotos)
  const checkAll = useSelector(selectCheckAll)
  const canFavorite = useSelector(selectCanFavorite)
  const isGroupPhotoViewLimit = useSelector(selectIsGroupPhotoViewLimit)
  const oldPhotosCount = useSelector(selectOldPhotosCount)
  const queryFilter = useSelector(selectSearchFilter)
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const myRole = useSelector((state: RootState) => state.group.myRole)
  const contentIsAscSort = useSelector(
    (state: RootState) => state.photosByContent.isAscSort
  )
  const userID = useSelector((state: any) => state.main?.user?.user_id || '')

  // 猜你想搜状态
  const suggestedWords = useSelector(selectSuggestedWords)
  const suggestedPhotos = useSelector(selectSuggestedPhotos)
  const suggestedPhotosNum = useSelector(selectSuggestedPhotosNum)
  const suggestedLastPageCond = useSelector(selectSuggestedLastPageCond)
  const suggestedCheckedPhotosRaw = useSelector(selectSuggestedCheckedPhotos)
  const suggestedUnCheckedPhotosRaw = useSelector(
    selectSuggestedUncheckedPhotos
  )
  const suggestedCheckAll = useSelector(selectSuggestedCheckAll)
  const fallbackType = useSelector(selectFallbackType)
  const fallbackKeyword = useSelector(selectFallbackKeyword)
  const suggestedQueryFilter = useSelector(selectSuggestedQueryFilter)

  const checkedPhotos = useMemo(
    () => normalizeToSet(checkedPhotosRaw),
    [checkedPhotosRaw]
  )
  const unCheckedPhotos = useMemo(
    () => normalizeToSet(unCheckedPhotosRaw),
    [unCheckedPhotosRaw]
  )
  const suggestedCheckedPhotos = useMemo(
    () => normalizeToSet(suggestedCheckedPhotosRaw),
    [suggestedCheckedPhotosRaw]
  )
  const suggestedUnCheckedPhotos = useMemo(
    () => normalizeToSet(suggestedUnCheckedPhotosRaw),
    [suggestedUnCheckedPhotosRaw]
  )
  const hasSuggestedSelected =
    (suggestedCheckAll && suggestedPhotosNum > suggestedUnCheckedPhotos.size) ||
    (!suggestedCheckAll && suggestedCheckedPhotos.size > 0)

  const hasMainSelected =
    (checkAll && photosNum > unCheckedPhotos.size) ||
    (!checkAll && checkedPhotos.size > 0)

  // 本地状态
  const [visibleBackTop, setVisibleBackTop] = useState(false)
  const [topMargin, setTopMargin] = useState(0)
  const fakePhotoCacheRef = useRef<{ [key: number]: PhotoType }>({})
  const [isVip, setIsVip] = useState(false)
  const [templatePhoto, setTemplatePhoto] = useState<PhotoType[]>([])

  const scrollFetching = useRef(false)
  const postListHeight = useRef(0)

  // 构建筛选成功埋点参数（用于猜你想搜场景）
  const buildTrackFilterSuccessParams = useCallback(
    (
      filter: typeof queryFilter,
      searchResult: any,
      processTime: number,
      searchTrackingId: string
    ) => {
      if (!filter) return null

      // 计算 tabList
      const tabList: number[] = [1] // 1 表示内容标签
      if (searchResult?.expandInfo?.expandPrecise?.canPreciseSearch) {
        tabList.push(3) // 3 表示精准搜索标签
      }

      // 获取搜索结果数据
      const firstPageNum = searchResult?.total || 0
      const recommendParticipleList =
        searchResult?.expandInfo?.expandSegment?.map(
          (item: any) => item.word
        ) || []
      const suggestedWords =
        searchResult?.suggestedSearchWords?.map((item: any) => item.word) || []

      // 生成推荐词来源
      const recommendedWordSource = Array(filter.searchKeywords.length)
        .fill('photo')
        .join('|')

      // 格式化时间规则
      const timeRule = filter.times
        ? [filter.times.map((item: any) => item.format('YY年M月D日')).join('-')]
        : []

      // 判断搜索类型：AI搜索 或 普通搜索
      const searchResultType = filter.isNlpFilter ? 'AI搜索' : '普通搜索'

      // 用户实际输入的文本
      const searchText = filter.searchKeywords?.join(' ') || ''

      // 获取文件类型
      const fileType =
        filter.mediaType === null
          ? 'all'
          : filter.mediaType === 0
            ? 'photo'
            : 'video'

      // 构建埋点参数
      const trackParams: any = {
        processTime,
        firstPageNum,
        recommendParticipleList,
        keyword: filter.searchKeywords?.join('|') || '',
        recommendedWordSource,
        list1: tabList,
        fromPlace: 'allPhoto',
        timeRule,
        number1: oldPhotosCount,
        list2: suggestedWords,
        string3: searchTrackingId,
        searchResultType,
        searchText,
        role: sensorsRole(myRole),
        groupID: groupId,
        fileType
      }

      // 如果是 AI 搜索，添加额外参数
      if (filter.isNlpFilter) {
        if (filter.departmentUsers && filter.departmentUsers.length > 0) {
          trackParams.list3 = filter.departmentUsers.map(
            (user: any) => user.name
          )
        }
      }

      return trackParams
    },
    [oldPhotosCount, myRole, groupId]
  )

  // 更新列表高度
  const updatePostListHeight = () => {
    const photoList = document.getElementsByClassName('photoList')[0]
    if (photoList) {
      postListHeight.current = photoList.clientHeight
      setTopMargin(postListHeight.current - 373)
    }
  }

  useEffect(() => {
    updatePostListHeight()
    window.addEventListener('resize', updatePostListHeight)
    return () => {
      window.removeEventListener('resize', updatePostListHeight)
    }
  }, [])

  const scrollGridToTop = useCallback(() => {
    if (photoListRef?.current?.scrollToPosition) {
      photoListRef.current.scrollToPosition({
        scrollLeft: 0,
        scrollTop: 0
      })
      return
    }

    if (photoListRef?.current?.scrollToRow) {
      photoListRef.current.scrollToRow(0)
      return
    }

    const gridDom = document.getElementsByClassName('virtualized-grid')[0] as
      | HTMLElement
      | undefined
    if (gridDom) {
      gridDom.scrollTop = 0
    }
  }, [photoListRef])

  // 当 queryFilter 变化时，获取 90 天前照片数量
  useEffect(() => {
    if (!queryFilter || !groupId) return

    const fetch90DaysCount = async () => {
      try {
        const params = buildFetchPhotosParamsWithOptions({
          queryFilter,
          groupID: groupId,
          isAscSort: false
        })
        const result = await (
          dispatch(fetch90DaysPhotosCountV2(params)) as any
        ).unwrap()
        if (result) {
          setIsVip(result.isVip === 1)
          const templatePhotos = (result.photoImageList || []).map((photo) => ({
            largeURL: photo,
            smallURL: photo,
            mediaType: 0,
            time: 1744631529000,
            userNickname: '测试用户',
            addr: '测试地址',
            seq: `template_${Math.random()}`
          }))
          setTemplatePhoto(templatePhotos)
        }
        if (result.count > 0 && !result.isVip) {
          trackPicsPageClickV2({ clickItem: 'showVip' })
        }
      } catch (error) {
        console.error('获取90天前照片数量失败:', error)
      }
    }

    fetch90DaysCount()
    fakePhotoCacheRef.current = {}
  }, [queryFilter, groupId, dispatch])

  // 当照片数据变化时，调用曝光埋点
  useEffect(() => {
    if (photos.length > 0) {
      trackExposureSensorEvent(photos, {
        groupID: groupId,
        myRole,
        queryFilter
      })
    }
  }, [photos, groupId, myRole, queryFilter])

  const searchTrackingId = (queryFilter as any)?.searchTrackingId ?? ''
  // 须等 fallbackType 写入后再报「出现补充结果」，避免 fetchSuggested 的 fulfilled 先更新条数、埋点仍读到 none
  const supplementReadyForExposeTrack =
    lastPageCond === '' &&
    suggestedPhotosNum > 0 &&
    (fallbackType === 'P1' || fallbackType === 'P2' || fallbackType === 'P3')
  const prevSupplementExposeRef = useRef(false)

  useEffect(() => {
    if (supplementReadyForExposeTrack && !prevSupplementExposeRef.current) {
      trackPicsPageClickV2({
        clickItem: '出现补充结果',
        string6: searchType,
        list1: suggestedWords.map((item) => item.word),
        ...(searchTrackingId ? { string3: searchTrackingId } : {})
      })
    }
    prevSupplementExposeRef.current = supplementReadyForExposeTrack
  }, [
    supplementReadyForExposeTrack,
    searchType,
    suggestedWords,
    searchTrackingId
  ])

  // 处理选中照片
  const handlePushCheckedPhoto = (photo: PhotoType) => {
    trackPicsPageClickV2({
      clickItem: 'chooseOnePhoto',
      isOpenErrorCorrection: queryFilter?.useSynonym
    })

    if (hasSuggestedSelected) {
      trackMutualExclusiveSelectionToast()
      message.warning('原始筛选结果与推荐结果不可同时选中')
      return
    }

    if (checkAll) {
      dispatch(removeUncheckedPhoto(photo.seq))
    } else {
      if (checkedPhotos.size === Math.max(photosNum - 1, 0)) {
        dispatch(setCheckAll())
      } else {
        dispatch(pushCheckedPhoto(photo.seq))
      }
    }
  }

  // 处理取消选中照片
  const handleRemoveCheckedPhoto = (photo: PhotoType) => {
    trackPicsPageClickV2({
      clickItem: 'cancelChooseOnePhoto',
      isOpenErrorCorrection: queryFilter?.useSynonym
    })

    if (checkAll) {
      if (unCheckedPhotos.size === Math.max(photosNum - 1, 0)) {
        dispatch(clearCheck())
      } else {
        dispatch(pushUncheckedPhoto(photo.seq))
      }
    } else {
      dispatch(removeCheckedPhoto(photo.seq))
    }
  }

  // 处理滚动
  const handleGridScroll = ({
    clientHeight,
    scrollHeight,
    scrollTop
  }: {
    clientHeight: number
    scrollHeight: number
    scrollTop: number
  }) => {
    if (scrollTop > window.screen.height - 210) {
      setVisibleBackTop(true)
    } else {
      setVisibleBackTop(false)
    }

    if (
      !scrollFetching.current &&
      parseInt(
        String(
          scrollHeight > clientHeight && scrollHeight - clientHeight - scrollTop
        )
      ) <= 0
    ) {
      if (lastPageCond) {
        handleFetchMore()
      } else if (suggestedLastPageCond) {
        handleFetchMoreSuggested()
      }
    }
  }

  // 处理返回顶部
  const handleBackTopClick = () => {
    const dom = document.getElementsByClassName(
      'virtualized-grid'
    )[0] as HTMLElement
    if (dom) {
      dom.scrollTop = 0
    }
  }

  // 加载更多照片
  const handleFetchMore = async () => {
    if (!lastPageCond || scrollFetching.current || !queryFilter || !groupId) {
      return
    }

    scrollFetching.current = true
    try {
      const params = buildFetchPhotosParamsWithOptions({
        queryFilter,
        groupID: groupId,
        lastPageCond,
        isAscSort: contentIsAscSort
      })

      const result = await (dispatch(fetchMorePhotosV2(params)) as any).unwrap()

      // 埋点
      if (result && result.photos && result.photos.length > 0) {
        trackExposureSensorEvent(result.photos, {
          groupID: groupId,
          myRole,
          queryFilter
        })
      }
    } catch (error) {
      capturePromiseException(error as Error)
    } finally {
      scrollFetching.current = false
    }
  }

  // 加载更多猜你想搜照片
  const handleFetchMoreSuggested = async () => {
    if (
      !suggestedLastPageCond ||
      scrollFetching.current ||
      (!queryFilter && !suggestedQueryFilter) ||
      !groupId
    ) {
      return
    }

    scrollFetching.current = true
    try {
      const mergedFilter = buildSuggestedPhotosQueryFilter(
        queryFilter,
        suggestedQueryFilter,
        suggestedWords
      )
      if (!mergedFilter) return

      const params = buildFetchPhotosParamsWithOptions({
        queryFilter: mergedFilter,
        groupID: groupId,
        lastPageCond: suggestedLastPageCond,
        isAscSort: false
      })

      await (dispatch(fetchMoreSuggestedPhotosV2(params)) as any).unwrap()
    } catch (error) {
      capturePromiseException(error as Error)
    } finally {
      scrollFetching.current = false
    }
  }

  // 处理搜索
  const handleSearch = async (
    newQueryFilter?: typeof queryFilter,
    index?: number,
    searchTrackingId?: string,
    isAscSort?: boolean
  ) => {
    if (!groupId) return

    const filter = newQueryFilter || queryFilter
    if (!filter) return

    dispatch(clearCheck())
    dispatch(setLoading(true))

    // 记录请求开始时间
    const requestStartTime = new Date().getTime()

    // 判断是否为猜你想搜操作，并确定操作类型
    let fromType: string | undefined
    if (
      newQueryFilter &&
      index !== undefined &&
      queryFilter?.suggestedSearchWords &&
      newQueryFilter.suggestedSearchWords
    ) {
      const oldWord = queryFilter.suggestedSearchWords[index]
      const newWord = newQueryFilter.suggestedSearchWords[index]
      if (
        oldWord &&
        newWord &&
        oldWord.word === newWord.word &&
        oldWord.isChecked !== newWord.isChecked
      ) {
        fromType = newWord.isChecked
          ? '全部照片页wantSearch'
          : '全部照片页cancelWantSearch'
      }
    }

    try {
      // [debug 2026-04-28] 排查"AI 推 location_key='北京' 但主页 v5 请求 locationRecommend={}"事故。
      // 这条路径(PhotosList useEffect 自动重拉)跟 photov2/index.tsx::handleFilterSubmit 平行,
      // 之前漏传 locationRecommend 导致 AI apply_page_filter 触发的首次重拉走错条件。
      // 跑通后(看到 {key:'北京', word:'北京'})可以删掉这段日志。
      if (filter.locationRecommend) {
        // eslint-disable-next-line no-console
        console.log(
          '[PhotosList/refetch] filter.locationRecommend =',
          filter.locationRecommend,
          'searchKeywords =',
          filter.searchKeywords,
          'departments.length =',
          filter.departments?.length ?? 0
        )
      }

      const params = buildFetchPhotosParamsWithOptions({
        queryFilter: filter,
        groupID: groupId,
        isAscSort: isAscSort || false,
        // 跟 photov2/index.tsx::handleFilterSubmit 那条 path 对齐:把 locationRecommend 透传到 v5。
        // 之前 PhotosList 这条 path 漏了,导致 AI apply_page_filter 推过来 location_key='北京'
        // 时主页 v5 请求 locationRecommend={},查到全员 30 天 1378 张而不是按"北京"过滤的 900 张。
        locationRecommend: filter.locationRecommend ?? null
      })

      const result = await (dispatch(fetchPhotosV2(params)) as any).unwrap()

      // 计算处理时间
      const processTime = new Date().getTime() - requestStartTime

      // 构建筛选成功埋点参数并上报（当有搜索关键词时才上报）
      if (
        filter.searchKeywords &&
        filter.searchKeywords.length > 0 &&
        result &&
        typeof result === 'object' &&
        'total' in result
      ) {
        const trackParams = buildTrackFilterSuccessParams(
          filter,
          result,
          processTime,
          searchTrackingId || ''
        )
        // 如果有 fromType，添加到埋点参数中
        if (fromType) {
          trackParams.fromType = fromType
        }
        trackFilterSuccess(trackParams)
      }

      // 曝光埋点
      if (result && result.photos && result.photos.length > 0) {
        trackExposureSensorEvent(result.photos, {
          groupID: groupId,
          myRole,
          queryFilter: filter
        })
      }
    } catch (error) {
      capturePromiseException(error as Error)
    } finally {
      dispatch(setLoading(false))
    }
  }

  // 处理排序变化
  const handleSortChange = async (isAscSort: boolean) => {
    if (!queryFilter || !groupId) return

    dispatch(setContentIsAscSort(isAscSort))
    dispatch(clearCheck())
    dispatch(setLoading(true))

    try {
      const params = buildFetchPhotosParamsWithOptions({
        queryFilter,
        groupID: groupId,
        isAscSort,
        // 排序变化时也要保留 locationRecommend,跟 handleFilterSubmit / refetch 路径对齐。
        // 不然用户点排序,location_key 就丢了。
        locationRecommend: queryFilter.locationRecommend ?? null
      })

      const result = await (dispatch(fetchPhotosV2(params)) as any).unwrap()

      // 埋点
      if (result && result.photos && result.photos.length > 0) {
        trackExposureSensorEvent(result.photos, {
          groupID: groupId,
          myRole,
          queryFilter
        })
      }
    } catch (error) {
      capturePromiseException(error as Error)
    } finally {
      dispatch(setLoading(false))
    }
    scrollGridToTop()
  }

  // 渲染单元格
  const renderCell = ({
    columnIndex,
    rowIndex,
    style,
    columnCount,
    rowsConfig,
    columnWidth,
    width
  }: {
    columnIndex: number
    rowIndex: number
    style: React.CSSProperties
    columnCount: number
    rowsConfig: any[]
    columnWidth: number
    width: number
  }) => {
    const rowConfig = rowsConfig[rowIndex]

    // 处理相近词行
    if (rowConfig?.type === 'fuzzyMatchRow') {
      if (columnIndex === 0) {
        return (
          <div style={{ margin: '0 auto', ...style, width }} key={rowIndex}>
            <FuzzyMatch
              onSensorOfClickEvent={(clickItem, restProperties) => {
                trackPicsPageClickV2({
                  clickItem,
                  isOpenErrorCorrection: queryFilter?.useSynonym,
                  ...restProperties
                })
              }}
            />
          </div>
        )
      }
      return null
    }

    // 处理真实照片
    if (rowConfig?.type === 'photoRow') {
      const photoRowStartIndex = rowsConfig.findIndex(
        (row) => row.type === 'photoRow'
      )
      const photoIndex =
        columnCount * (rowIndex - photoRowStartIndex) + columnIndex
      const photo = photos[photoIndex]
      if (!photo) return null

      const isChecked = checkAll
        ? !unCheckedPhotos.has(photo.seq)
        : checkedPhotos.has(photo.seq)

      return (
        <div
          style={{ margin: '0 auto', ...style }}
          key={rowIndex + '' + photoIndex}
        >
          <PhotoWithTag
            style={{ margin: 0 }}
            photo={photo as any}
            canCheck
            readyToCheck={
              checkedPhotos.size > 0 || unCheckedPhotos.size > 0 || checkAll
            }
            onPreview={() => {
              trackPicsPageClickV2({
                clickItem: 'photo',
                photoDateTime: photo.time || '',
                PassiveUserid: photo.userID || '',
                tabName: '1',
                imageUrl: photo.largeURL,
                number3: photo.createdAt,
                string6: photo.seq,
                string7: photo.time,
                popType: getSensorContent(photo.sourceType),
                isOpenErrorCorrection: queryFilter?.useSynonym
              })
              if (viewerRef.current) {
                viewerRef.current.view(photoIndex)
              }
            }}
            checked={isChecked}
            onCheck={() => handlePushCheckedPhoto(photo)}
            onUnCheck={() => handleRemoveCheckedPhoto(photo)}
            track={(clickItem, restProperties) => {
              trackPicsPageClickV2({
                clickItem,
                isOpenErrorCorrection: queryFilter?.useSynonym,
                ...restProperties
              })
            }}
          />
        </div>
      )
    }

    // 处理 90 天前标题行
    if (rowConfig?.type === '90DaysTitleRow') {
      if (columnIndex === 0) {
        const marginTopValue = photos.length === 0 ? topMargin : 0
        return (
          <div
            style={{
              margin: '0 auto',
              ...style,
              width,
              padding: '15px 0',
              marginTop: marginTopValue
            }}
            key={'90-day-row'}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#666',
                fontWeight: 'normal'
              }}
            >
              90天前·共筛选出 {oldPhotosCount} 张照片
            </div>
          </div>
        )
      }
      return null
    }

    // 处理假照片行
    if (rowConfig?.type === '90DaysFakePhotoRow') {
      const maxFakePhotos = Math.min(oldPhotosCount, columnCount)
      if (columnIndex < maxFakePhotos) {
        let templatePhotoItem: PhotoType | null = null

        if (!fakePhotoCacheRef.current[columnIndex]) {
          if (photos.length < columnCount && templatePhoto.length > 0) {
            const randomIndex = Math.floor(Math.random() * templatePhoto.length)
            templatePhotoItem = templatePhoto[randomIndex]
          } else if (photos.length > 0) {
            const randomIndex = Math.floor(Math.random() * photos.length)
            templatePhotoItem = photos[randomIndex]
          } else {
            return null
          }

          // 使用 ref 来存储，避免在渲染期间触发状态更新
          fakePhotoCacheRef.current[columnIndex] = templatePhotoItem
        } else {
          templatePhotoItem = fakePhotoCacheRef.current[columnIndex]
        }

        if (templatePhotoItem) {
          const fakeTopMargin =
            photos.length === 0 && rowIndex === 1 ? topMargin - 10 : 0

          return (
            <div
              style={{
                margin: '0 auto',
                ...style,
                marginTop: rowIndex === 1 ? fakeTopMargin : 0
              }}
              key={`fake-row-${columnIndex}`}
            >
              <div>
                <Photo
                  style={{ margin: 0, filter: 'blur(3px)' }}
                  photo={templatePhotoItem as any}
                  canCheck={false}
                  checked={false}
                  readyToCheck={false}
                  onPreview={() => {
                    /* 推荐区占位卡片不支持预览 */
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 10
                  }}
                />
              </div>
              {columnIndex === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${columnCount * columnWidth}px`,
                    height: style.height || 280,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 12,
                    pointerEvents: 'none'
                  }}
                >
                  <div style={{ pointerEvents: 'auto' }}>
                    <OpenVip
                      onSensorOfClickEvent={(clickItem, restProperties) => {
                        trackPicsPageClickV2({
                          clickItem,
                          isOpenErrorCorrection: queryFilter?.useSynonym,
                          ...restProperties
                        })
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        }
      }
      return null
    }

    // 处理大团队照片查看限制行
    if (rowConfig?.type === 'groupPhotoViewLimitRow') {
      if (columnIndex === 0) {
        return (
          <div
            style={{ margin: '0 auto', ...style, width }}
            key="group-photo-view-limit-row"
          >
            <OpenEnterprise columnCount={columnCount} />
          </div>
        )
      }
      return null
    }

    // 处理猜你想搜标题行
    if (rowConfig?.type === 'emptyPlaceholderRow') {
      if (columnIndex === 0) {
        return (
          <div
            style={{
              ...style,
              width,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '180px',
              paddingTop: '20px',
              paddingBottom: '20px'
            }}
            key="empty-placeholder-row"
          >
            <Empty description="无照片" />
          </div>
        )
      }
      return null
    }

    // 处理猜你想搜标题行
    if (rowConfig?.type === 'suggestedHeaderRow') {
      if (columnIndex === 0) {
        let titleText = `可能相关的结果 ${suggestedPhotosNum} 张照片`
        if (fallbackType === 'P2') {
          titleText = fallbackKeyword
            ? `找到其他时间的「${fallbackKeyword}」 ${suggestedPhotosNum} 张照片`
            : `找到其他时间的结果 ${suggestedPhotosNum} 张照片`
        } else if (fallbackType === 'P3') {
          titleText = fallbackKeyword
            ? `找到其他人的「${fallbackKeyword}」 ${suggestedPhotosNum} 张照片`
            : `找到其他人的结果 ${suggestedPhotosNum} 张照片`
        }

        return (
          <div
            style={{
              ...style,
              width,
              display: 'flex',
              alignItems: 'center',
              paddingTop: '20px', // 根据原型图，上方有一点间距
              fontSize: '14px',
              color: '#848484'
            }}
            key="suggested-header-row"
          >
            <span style={{ marginRight: '16px' }}>{titleText}</span>
            <Checkbox
              style={{ marginRight: '16px' }}
              checked={suggestedCheckAll}
              onChange={(e) => {
                if (e.target.checked && hasMainSelected) {
                  trackMutualExclusiveSelectionToast()
                  message.warning('原始筛选结果与推荐结果不可同时选中')
                  return
                }
                trackPicsPageClickV2({
                  clickItem: e.target.checked ? 'chooseAll' : 'cancelChooseAll',
                  tabName: 'suggested'
                })
                dispatch(setSuggestedCheckAll(e.target.checked))
              }}
            >
              全选
            </Checkbox>
            <span>
              已选择{' '}
              {suggestedCheckAll
                ? suggestedPhotosNum - suggestedUnCheckedPhotos.size
                : suggestedCheckedPhotos.size}{' '}
              张照片
            </span>
          </div>
        )
      }
      return null
    }

    // 处理猜你想搜标签行
    if (rowConfig?.type === 'suggestedTagsRow') {
      if (columnIndex === 0) {
        return (
          <div
            style={{
              ...style,
              width,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
            key="suggested-tags-row"
          >
            {suggestedWords.map((item, index) => {
              const checkedCount = suggestedWords.filter(
                (word) => word.isChecked
              ).length
              // 如果只剩下一个选中的，禁止取消勾选
              const shouldPreventToggle = item.isChecked && checkedCount === 1

              return (
                <span
                  key={index}
                  style={{
                    marginRight: '16px',
                    cursor: shouldPreventToggle ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Checkbox
                    checked={item.isChecked}
                    disabled={shouldPreventToggle}
                    onChange={(e) => {
                      if (shouldPreventToggle) return
                      const nextChecked = e.target.checked

                      dispatch(
                        updateSuggestedWordChecked({
                          index,
                          isChecked: nextChecked
                        })
                      )

                      // 触发重新搜索猜你想搜照片
                      const nextSuggestedWords = [...suggestedWords]
                      nextSuggestedWords[index] = {
                        ...item,
                        isChecked: nextChecked
                      }
                      const mergedFilter = buildSuggestedPhotosQueryFilter(
                        queryFilter,
                        suggestedQueryFilter,
                        nextSuggestedWords
                      )
                      if (!mergedFilter) return
                      const params = buildFetchPhotosParamsWithOptions({
                        queryFilter: mergedFilter,
                        groupID: groupId!,
                        isAscSort: false
                      })
                      dispatch(fetchSuggestedPhotosV2(params) as any)
                    }}
                  >
                    <span
                      style={{
                        color: item.isChecked ? '#1890ff' : '#000000A6'
                      }}
                    >
                      {item.word}
                    </span>
                  </Checkbox>
                </span>
              )
            })}
          </div>
        )
      }
      return null
    }

    // 处理猜你想搜照片行
    if (rowConfig?.type === 'suggestedPhotoRow') {
      const suggestedRowStartIndex = rowsConfig.findIndex(
        (row) => row.type === 'suggestedPhotoRow'
      )
      const photoIndex =
        columnCount * (rowIndex - suggestedRowStartIndex) + columnIndex
      const photo = suggestedPhotos[photoIndex]
      if (!photo) return null

      const isChecked = suggestedCheckAll
        ? !suggestedUnCheckedPhotos.has(photo.seq)
        : suggestedCheckedPhotos.has(photo.seq)

      return (
        <div
          style={{ margin: '0 auto', ...style }}
          key={`suggested-photo-${photo.seq}`}
        >
          <PhotoWithTag
            style={{ margin: 0 }}
            photo={photo as any}
            canCheck
            readyToCheck={
              suggestedCheckedPhotos.size > 0 ||
              suggestedUnCheckedPhotos.size > 0 ||
              suggestedCheckAll
            }
            onPreview={() => {
              // 猜你想搜照片预览埋点
              trackPicsPageClickV2({
                clickItem: 'photo',
                photoDateTime: photo.time || '',
                PassiveUserid: photo.userID || '',
                tabName: 'suggested',
                imageUrl: photo.largeURL,
                string6: photo.seq,
                popType: getSensorContent(photo.sourceType)
              })

              if (suggestedViewerRef.current) {
                if (typeof suggestedViewerRef.current.view === 'function') {
                  suggestedViewerRef.current.view(photoIndex)
                } else {
                  console.error(
                    'Viewer component ref does not expose view method:',
                    suggestedViewerRef.current
                  )
                }
              }
            }}
            checked={isChecked}
            onCheck={() => {
              if (hasMainSelected) {
                trackMutualExclusiveSelectionToast()
                message.warning('原始筛选结果与推荐结果不可同时选中')
                return
              }
              trackPicsPageClickV2({
                clickItem: 'chooseOnePhoto',
                tabName: 'suggested',
                isOpenErrorCorrection: queryFilter?.useSynonym
              })
              if (suggestedCheckAll) {
                dispatch(removeSuggestedUncheckedPhoto(photo.seq))
              } else {
                dispatch(pushSuggestedCheckedPhoto(photo.seq))
              }
            }}
            onUnCheck={() => {
              trackPicsPageClickV2({
                clickItem: 'cancelChooseOnePhoto',
                tabName: 'suggested',
                isOpenErrorCorrection: queryFilter?.useSynonym
              })
              if (suggestedCheckAll) {
                dispatch(pushSuggestedUncheckedPhoto(photo.seq))
              } else {
                dispatch(removeSuggestedCheckedPhoto(photo.seq))
              }
            }}
          />
        </div>
      )
    }

    return null
  }

  // 生成行配置
  const generateRowsConfig = ({
    photoNum,
    columnCount,
    hasFuzzyMatch,
    isGroupPhotoViewLimit,
    oldPhotosCount,
    lastPageCond,
    suggestedWords,
    suggestedPhotoNum
  }: {
    photoNum: number
    columnCount: number
    hasFuzzyMatch: boolean
    isGroupPhotoViewLimit: boolean
    oldPhotosCount: number
    lastPageCond: string
    suggestedWords: any[]
    suggestedPhotoNum: number
  }) => {
    const rows: any[] = []
    const photoRows = columnCount > 0 ? Math.ceil(photoNum / columnCount) : 0

    const showLockedPhotos =
      lastPageCond === '' &&
      oldPhotosCount > 0 &&
      !isVip &&
      !isGroupPhotoViewLimit

    if (hasFuzzyMatch) {
      rows.push({
        type: 'fuzzyMatchRow',
        height: 40
      })
    }

    if (photoRows > 0) {
      rows.push(
        ...Array(photoRows).fill({
          type: 'photoRow',
          height: 280
        })
      )
    }

    if (isGroupPhotoViewLimit) {
      rows.push({
        type: 'groupPhotoViewLimitRow',
        height: 280
      })
    } else if (showLockedPhotos) {
      rows.push({
        type: '90DaysTitleRow',
        height: 50
      })
      rows.push({
        type: '90DaysFakePhotoRow',
        height: 280
      })
    }

    // 插入猜你想搜及无结果推荐逻辑（列表最底部）
    if (
      lastPageCond === '' &&
      (suggestedPhotoNum > 0 || (suggestedWords && suggestedWords.length > 0))
    ) {
      // 在有降级推荐的情况下，如果原始照片为 0，插入无照片图标行，并且高度设置为一定的值（占位用）
      if (photoNum === 0) {
        rows.push({
          type: 'emptyPlaceholderRow',
          height: 180
        })
      }

      // 拆分为两行展示（无背景，无 padding，还原原型图）
      rows.push({
        type: 'suggestedHeaderRow',
        height: 50 // 原型图行高
      })
      if (suggestedWords && suggestedWords.length > 0) {
        rows.push({
          type: 'suggestedTagsRow',
          height: 40 // 原型图行高
        })
      }
      // 照片行
      if (suggestedPhotoNum > 0) {
        const suggestedPhotoRows =
          columnCount > 0 ? Math.ceil(suggestedPhotoNum / columnCount) : 0
        rows.push(
          ...Array(suggestedPhotoRows).fill({
            type: 'suggestedPhotoRow',
            height: 280
          })
        )
      }
    }

    return rows
  }

  // 计算相关数据
  const hasFuzzyMatch = (expandInfo?.expandSynonym?.total || 0) > 0
  const columnWidth = 210
  const showLockedPhotos =
    lastPageCond === '' &&
    oldPhotosCount > 0 &&
    !isVip &&
    !isGroupPhotoViewLimit
  const useSimpleFillLayout =
    photos.length > 0 &&
    photos.length <= 4 &&
    !hasFuzzyMatch &&
    !isGroupPhotoViewLimit &&
    !showLockedPhotos &&
    suggestedPhotos.length === 0

  // ============ 批量打标模式 (BatchTagMode) 集成 ============
  // 与 photov2/PhotosList.tsx 同一份实现, 复用同一份 redux slice (selectCheckedPhotos / setCheckAll / clearCheck 等)
  const store = useStore()
  const exclude: string[] = useMemo(() => {
    if (checkAll) {
      return photos
        .filter((p: any) => unCheckedPhotos.has(p.seq))
        .map((p: any) => p.file)
    }
    return []
  }, [checkAll, photos, unCheckedPhotos])
  const include: string[] = useMemo(() => {
    if (!checkAll) {
      return photos
        .filter((p: any) => checkedPhotos.has(p.seq))
        .map((p: any) => p.file)
    }
    return []
  }, [checkAll, photos, checkedPhotos])
  // 「已选 N 张」要反映用户真实意图:
  // - checkAll=true: 等于筛选命中总数 photosNum - 用户主动取消的张数,
  //   不能用 photos.length (那是已虚拟加载的 visible 张数,跟筛选总数无关)。
  //   后端 batchBindLabels 按 filter + exclude 整体匹配,真实打标范围就是这个数。
  // - checkAll=false: 用户手动勾选了多少张就是多少张。
  const batchSelectedCount = useMemo(() => {
    if (checkAll) return Math.max(0, photosNum - unCheckedPhotos.size)
    return checkedPhotos.size
  }, [checkAll, photosNum, unCheckedPhotos.size, checkedPhotos.size])

  const { tags: teamAllTagsForBatch } = useTeamPhotoTags()

  // 批量打标 handlers — 抽到 useBatchTagHandlers hook (跟 photov2 共用), review #4
  const { applyTagsHandler, unbindTagsHandler } = useBatchTagHandlers({
    queryFilter,
    groupId,
    photos,
    checkAll,
    unCheckedPhotos,
    checkedPhotos,
    include,
    exclude,
    teamAllTagsForBatch,
    buildFetchPhotosParamsWithOptions,
    fromPage: 'ai_photo_king'
  })

  return (
    <BatchTagModeProvider
      applyTagsHandler={applyTagsHandler}
      unbindTagsHandler={unbindTagsHandler}
      pushChecked={(seq) => dispatch(pushCheckedPhoto(seq))}
      onExit={() => dispatch(clearCheck())}
      fromPage="ai_photo_king"
    >
      <div className="photoList">
        <HeaderExtra
          dataSource="content"
          onSearch={handleSearch}
          onSortChange={handleSortChange}
          isAsc={contentIsAscSort}
          searchType={searchType}
        />
        {useSimpleFillLayout ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${photos.length}, ${columnWidth}px)`,
              gap: '20px',
              alignItems: 'start',
              justifyContent: 'start',
              width: '100%',
              paddingTop: '12px',
              isolation: 'isolate'
            }}
          >
            {photos.map((photo, photoIndex) => {
              const isChecked = checkAll
                ? !unCheckedPhotos.has(photo.seq)
                : checkedPhotos.has(photo.seq)

              return (
                <div
                  key={`simple-photo-${photo.seq}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    minWidth: 0
                  }}
                >
                  <PhotoWithTag
                    style={{ margin: 0 }}
                    photo={photo as any}
                    canCheck
                    readyToCheck={
                      checkedPhotos.size > 0 ||
                      unCheckedPhotos.size > 0 ||
                      checkAll
                    }
                    onPreview={() => {
                      trackPicsPageClickV2({
                        clickItem: 'photo',
                        photoDateTime: photo.time || '',
                        PassiveUserid: photo.userID || '',
                        tabName: '1',
                        imageUrl: photo.largeURL,
                        number3: photo.createdAt,
                        string6: photo.seq,
                        string7: photo.time,
                        popType: getSensorContent(photo.sourceType),
                        isOpenErrorCorrection: queryFilter?.useSynonym
                      })
                      if (viewerRef.current) {
                        viewerRef.current.view(photoIndex)
                      }
                    }}
                    checked={isChecked}
                    onCheck={() => handlePushCheckedPhoto(photo)}
                    onUnCheck={() => handleRemoveCheckedPhoto(photo)}
                    track={(clickItem, restProperties) => {
                      trackPicsPageClickV2({
                        clickItem,
                        isOpenErrorCorrection: queryFilter?.useSynonym,
                        ...restProperties
                      })
                    }}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div
            className="photoList__virtual-body"
            style={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative' }}
          >
            <AutoSizer>
              {({ width, height }) => {
                const columnCount = Math.floor(width / columnWidth)
                const rowsConfig = generateRowsConfig({
                  photoNum: photos.length,
                  columnCount,
                  hasFuzzyMatch,
                  isGroupPhotoViewLimit,
                  oldPhotosCount,
                  lastPageCond,
                  suggestedWords,
                  suggestedPhotoNum: suggestedPhotos.length
                })
                const rowCount = rowsConfig.length

                return (
                  <Grid
                    className="virtualized-grid"
                    ref={photoListRef}
                    style={{
                      outline: 'none',
                      scrollBehavior: 'smooth',
                      // 留 16px 喘息;之前是 100,会把最后一排照片挤出可视区,DevTools 显示紫条
                      paddingBottom: 16
                    }}
                    width={width}
                    height={height}
                    columnWidth={columnWidth}
                    rowHeight={({ index }) => {
                      return rowsConfig[index]?.height || 0
                    }}
                    columnCount={columnCount}
                    rowCount={rowCount}
                    cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                      renderCell({
                        columnIndex,
                        rowIndex,
                        style,
                        columnCount,
                        rowsConfig,
                        columnWidth,
                        width
                      })
                    }
                    onScroll={handleGridScroll}
                  />
                )
              }}
            </AutoSizer>
          </div>
        )}
        <div
          className="isTop"
          style={{ opacity: visibleBackTop ? '1' : '0', right: '56px' }}
        >
          <img
            style={{ width: '48px' }}
            src={backTop}
            onClick={handleBackTopClick}
            alt=""
          />
        </div>
        {!photos.length &&
          !isGroupPhotoViewLimit &&
          fallbackType === 'none' &&
          suggestedPhotosNum === 0 &&
          (!suggestedWords || suggestedWords.length === 0) && (
            <div
              className="empty-wrapper"
              style={{
                marginTop: topMargin > 0 ? topMargin / 2 - 40 : '5vh'
              }}
            >
              <Empty description="无照片" />
            </div>
          )}
      </div>
      <Viewer
        ref={viewerRef}
        // @ts-expect-error ViewerV2 的 photos 类型与本页 Photo[] 不完全一致
        photos={photos}
        onFetchMore={handleFetchMore}
        canFavorite={canFavorite}
        showWatermarkAction={true}
        isShowComments={true}
        fromPlace="allPhoto"
        // @ts-expect-error afterFavoriteChange 非 ViewerProps 标准属性
        afterFavoriteChange={(seq: number, favorite: boolean) => {
          dispatch(
            updatePhotoFavorite({
              seq,
              isFavorite: favorite
            })
          )
        }}
      />
      <Viewer
        ref={suggestedViewerRef}
        // @ts-expect-error ViewerV2 的 photos 类型与本页 Photo[] 不完全一致
        photos={suggestedPhotos}
        onFetchMore={handleFetchMoreSuggested}
        canFavorite={canFavorite}
        showWatermarkAction={true}
        isShowComments={true}
        fromPlace="allPhoto"
      />
      <BatchTagBar
        selectedCount={batchSelectedCount}
        groupID={String(groupId || '')}
        userID={String(userID || '')}
        isSelectAll={checkAll}
        onSelectAll={() => dispatch(checkAll ? clearCheck() : setCheckAll())}
      />
    </BatchTagModeProvider>
  )
}

export default PhotosList
